import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
} from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { useAuth } from "@/components/providers/AuthProvider";
import { useFetchCurrentUserProfileQuery } from "@/api/clubsApi";
import { OnboardingModal } from "./OnboardingModal";

/** Keep the portal mounted long enough for the exit animation to play. */
function useDelayedMount(open: boolean, closeDelayMs = 300) {
  const [mounted, setMounted] = useState(open);
  useEffect(() => {
    if (open) {
      setMounted(true);
    } else {
      const id = setTimeout(() => setMounted(false), closeDelayMs);
      return () => clearTimeout(id);
    }
  }, [open, closeDelayMs]);
  return mounted;
}

interface OnboardingContextType {
  isOpen: boolean;
  closeOnboarding: () => void;
}

const OnboardingContext = createContext<OnboardingContextType | null>(null);

export function useOnboarding() {
  const context = useContext(OnboardingContext);
  if (!context) {
    throw new Error("useOnboarding must be used within OnboardingProvider");
  }
  return context;
}

export function OnboardingProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const { user, isLoading: isAuthLoading } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const mounted = useDelayedMount(isOpen);

  // Fetch the user's profile to check onboarding_complete
  const { data: profile, isLoading: isProfileLoading } =
    useFetchCurrentUserProfileQuery(user?.id ?? "", {
      skip: !user?.id,
    });

  // Auto-open when authenticated user has not completed onboarding
  useEffect(() => {
    if (isAuthLoading || isProfileLoading) return;
    if (!user) {
      setIsOpen(false);
      return;
    }
    if (profile && profile.onboarding_complete !== true) {
      setIsOpen(true);
    } else {
      setIsOpen(false);
    }
  }, [user, profile, isAuthLoading, isProfileLoading]);

  const closeOnboarding = useCallback(() => {
    setIsOpen(false);
  }, []);

  return (
    <OnboardingContext.Provider value={{ isOpen, closeOnboarding }}>
      {children}
      <DialogPrimitive.Root open={isOpen}>
        {mounted && (
          <DialogPrimitive.Portal forceMount>
            <DialogPrimitive.Overlay
              className="fixed inset-0 z-50 bg-black/[0.08] backdrop-blur-[6px]"
              data-state={isOpen ? "open" : "closed"}
              style={{
                animation: isOpen
                  ? "fadeIn 200ms ease-out"
                  : "fadeOut 200ms ease-in",
              }}
            />
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
              <DialogPrimitive.Content
                className="w-full max-w-[568px] h-[800px] max-h-[calc(100vh-200px)] rounded-[32px] border border-border-tertiary bg-background overflow-hidden flex flex-col"
                style={{ boxShadow: "0 8px 40px rgba(0,0,0,0.12)" }}
                data-state={isOpen ? "open" : "closed"}
                aria-describedby={undefined}
                onPointerDownOutside={(e) => e.preventDefault()}
                onEscapeKeyDown={(e) => e.preventDefault()}
                onInteractOutside={(e) => e.preventDefault()}
              >
                <DialogPrimitive.Title className="sr-only">
                  Welcome to TinyClub
                </DialogPrimitive.Title>
                <OnboardingModal closeOnboarding={closeOnboarding} />
              </DialogPrimitive.Content>
            </div>
          </DialogPrimitive.Portal>
        )}
      </DialogPrimitive.Root>
    </OnboardingContext.Provider>
  );
}
